import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Axios from 'axios';

import { useContactExternalAccess } from '@hooks/API/Contact/UseContactExternalAccess';
import {
    useAllowContactAccess,
    useRevokeContactAccess,
    useUpdateContactAccess,
} from '@hooks/API/Contact/UseModelDataUpdateRequests';

import { buildModelDataUpdatesLink } from '@containers/Booking/ModelDataUpdates/modelDataUpdates-route.helpers';

import {
    buildUpdateRequestEmailHref,
    formatDateWithActor,
    getApiErrorMessage,
    getInitialSelectedEmail,
    getUpdateRequestStatusBadge,
    parseConnectionsEmails,
} from './contactDetailsActions.utils';
import {
    isImportedUpdateRequestStatus,
    isPendingSubmittedUpdateRequestStatus,
    normalizeUpdateRequestStatus,
    parseExternalAccessResponse,
} from './contactDetails-externalAccess.helpers';

const useExternalAccessActionItem = ({ contactId, defaultEmail = '', onRequestClose }) => {
    const navigate = useNavigate();
    const [availableEmails, setAvailableEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState('');
    const [isLoadingEmails, setIsLoadingEmails] = useState(false);
    const [emailsLoadError, setEmailsLoadError] = useState('');
    const [showExternalAccessUpdateForm, setShowExternalAccessUpdateForm] = useState(false);
    const [showSendUpdateRequestDrawer, setShowSendUpdateRequestDrawer] = useState(false);
    const [externalAccessActionError, setExternalAccessActionError] = useState('');
    const [externalAccessActionSuccess, setExternalAccessActionSuccess] = useState('');

    const emailsRequestIdRef = useRef(0);
    const isMountedRef = useRef(true);

    const normalizedContactId = Number(contactId);
    const hasValidContactId = Number.isFinite(normalizedContactId) && normalizedContactId > 0;

    const {
        data: externalAccessResponse,
        isFetching: isLoadingExternalAccess,
        error: externalAccessQueryError,
    } = useContactExternalAccess(hasValidContactId ? normalizedContactId : null);

    const { mutateAsync: allowContactAccess } = useAllowContactAccess();
    const { mutateAsync: updateContactAccess } = useUpdateContactAccess();
    const { mutateAsync: revokeContactAccess } = useRevokeContactAccess();

    const [isSendingExternalRequest, setIsSendingExternalRequest] = useState(false);
    const [isUpdatingExternalAccess, setIsUpdatingExternalAccess] = useState(false);
    const [isRevokingExternalAccess, setIsRevokingExternalAccess] = useState(false);

    const parsedExternalAccess = useMemo(
        () => parseExternalAccessResponse(externalAccessResponse ?? null),
        [externalAccessResponse]
    );
    const externalAccessExists = parsedExternalAccess.access.exists;
    const externalAccessActive = parsedExternalAccess.access.active;
    const externalAccessEmail = parsedExternalAccess.access.email;
    const externalAccessActivationDate = parsedExternalAccess.access.activationDate;
    const externalAccessUpdateRequest = parsedExternalAccess.updateRequest;

    const externalAccessLoadError = emailsLoadError || (externalAccessQueryError ? getApiErrorMessage(externalAccessQueryError) : '');

    const getInitialModelEmail = useCallback(
        (emails, currentAccessEmail = '') => getInitialSelectedEmail(emails, currentAccessEmail, defaultEmail),
        [defaultEmail]
    );

    // Keep the selected email in sync with the resolved access data without triggering
    // an extra request: `useContactExternalAccess` already shares the cache across consumers.
    useEffect(() => {
        if (availableEmails.length === 0) {
            return;
        }
        setSelectedEmail((currentSelectedEmail) => {
            if (currentSelectedEmail && availableEmails.includes(currentSelectedEmail)) {
                return currentSelectedEmail;
            }
            return getInitialModelEmail(availableEmails, externalAccessEmail);
        });
    }, [availableEmails, externalAccessEmail, getInitialModelEmail]);

    // Load the list of available emails (connections). This is independent from the
    // external access query and stays imperative to keep the refactor focused.
    useEffect(() => {
        isMountedRef.current = true;

        if (!hasValidContactId) {
            setAvailableEmails([]);
            setSelectedEmail('');
            setEmailsLoadError('');
            return () => {
                isMountedRef.current = false;
            };
        }

        const requestId = emailsRequestIdRef.current + 1;
        emailsRequestIdRef.current = requestId;
        setIsLoadingEmails(true);
        setEmailsLoadError('');

        Axios.post('/contacts/connections/emails', { ContactIDs: [normalizedContactId] })
            .then((response) => {
                if (!isMountedRef.current || requestId !== emailsRequestIdRef.current) {
                    return;
                }
                const parsedEmails = parseConnectionsEmails(response);
                setAvailableEmails(parsedEmails);
            })
            .catch((error) => {
                if (!isMountedRef.current || requestId !== emailsRequestIdRef.current) {
                    return;
                }
                setEmailsLoadError(getApiErrorMessage(error));
            })
            .finally(() => {
                if (isMountedRef.current && requestId === emailsRequestIdRef.current) {
                    setIsLoadingEmails(false);
                }
            });

        return () => {
            isMountedRef.current = false;
        };
    }, [hasValidContactId, normalizedContactId]);

    const handleSendUpdateRequest = () => {
        setExternalAccessActionError('');
        setExternalAccessActionSuccess('');

        if (externalAccessUpdateRequest && !isImportedUpdateRequestStatus(externalAccessUpdateRequest.Status)) {
            setExternalAccessActionError('the latest update request is not imported yet');
            return;
        }

        if (!externalAccessActive) {
            setExternalAccessActionError('external access is not active');
            return;
        }

        setShowSendUpdateRequestDrawer(true);
    };

    const handleEnableAndOpenSendUpdateRequestDrawer = async () => {
        if (externalAccessUpdateRequest && !isImportedUpdateRequestStatus(externalAccessUpdateRequest.Status)) {
            setExternalAccessActionError('the latest update request is not imported yet');
            return;
        }

        if (!selectedEmail) {
            setExternalAccessActionError('select an e-mail');
            return;
        }

        setExternalAccessActionError('');
        setExternalAccessActionSuccess('');
        setIsSendingExternalRequest(true);

        try {
            await allowContactAccess({
                contactId: normalizedContactId,
                email: selectedEmail,
                enableUpdates: true,
            });
            if (isMountedRef.current) {
                setShowSendUpdateRequestDrawer(true);
            }
        } catch (error) {
            if (isMountedRef.current) {
                setExternalAccessActionError(getApiErrorMessage(error));
            }
        } finally {
            if (isMountedRef.current) {
                setIsSendingExternalRequest(false);
            }
        }
    };

    const handleUpdateExternalAccess = async () => {
        if (!selectedEmail) {
            setExternalAccessActionError('select an e-mail');
            return;
        }

        setExternalAccessActionError('');
        setExternalAccessActionSuccess('');
        setIsUpdatingExternalAccess(true);

        try {
            await updateContactAccess({
                contactId: normalizedContactId,
                email: selectedEmail,
                enableUpdates: true,
            });
            if (isMountedRef.current) {
                setExternalAccessActionSuccess('access e-mail updated');
                setShowExternalAccessUpdateForm(false);
            }
        } catch (error) {
            if (isMountedRef.current) {
                setExternalAccessActionError(getApiErrorMessage(error));
            }
        } finally {
            if (isMountedRef.current) {
                setIsUpdatingExternalAccess(false);
            }
        }
    };

    const handleRevokeExternalAccess = async () => {
        setExternalAccessActionError('');
        setExternalAccessActionSuccess('');
        setIsRevokingExternalAccess(true);

        try {
            await revokeContactAccess({ contactId: normalizedContactId });
            if (isMountedRef.current) {
                setExternalAccessActionSuccess('access revoked');
            }
        } catch (error) {
            if (isMountedRef.current) {
                setExternalAccessActionError(getApiErrorMessage(error));
            }
        } finally {
            if (isMountedRef.current) {
                setIsRevokingExternalAccess(false);
            }
        }
    };

    const handleToggleExternalAccessUpdateForm = () => {
        if (showExternalAccessUpdateForm) {
            setShowExternalAccessUpdateForm(false);
            return;
        }

        setSelectedEmail(getInitialModelEmail(availableEmails, externalAccessEmail));
        setShowExternalAccessUpdateForm(true);
    };

    const handleOpenSubmittedUpdateReview = () => {
        if (!hasValidContactId || !isPendingSubmittedUpdateRequestStatus(externalAccessUpdateRequest?.Status)) {
            return;
        }

        const submittedUpdatePath = buildModelDataUpdatesLink({
            updateId: externalAccessUpdateRequest?.ID,
            contactId: normalizedContactId,
        });

        if (onRequestClose) {
            onRequestClose();
        }
        navigate(submittedUpdatePath);
    };

    const hasAvailableEmails = availableEmails.length > 0;
    const normalizedExternalAccessEmail = `${externalAccessEmail || ''}`.trim().toLowerCase();
    const normalizedSelectedEmail = `${selectedEmail || ''}`.trim().toLowerCase();
    const updateRequestStatus = normalizeUpdateRequestStatus(externalAccessUpdateRequest?.Status);
    const isSentUpdateRequest = updateRequestStatus === 'draft';
    const isImportedUpdateRequest = isImportedUpdateRequestStatus(externalAccessUpdateRequest?.Status);
    const canSendUpdateRequest = !externalAccessUpdateRequest || isImportedUpdateRequest;
    const canUpdateExternalAccess =
        externalAccessActive &&
        Boolean(normalizedSelectedEmail) &&
        normalizedSelectedEmail !== normalizedExternalAccessEmail;
    const isExternalAccessActionInProgress =
        isSendingExternalRequest || isUpdatingExternalAccess || isRevokingExternalAccess;
    const hasPendingSubmittedUpdateRequest = isPendingSubmittedUpdateRequestStatus(externalAccessUpdateRequest?.Status);
    const hasSubmittedUpdateRequest = updateRequestStatus === 'submittedtobooker' || isImportedUpdateRequest;
    const hasImportedUpdateRequest = isImportedUpdateRequest;
    const importedByName =
        externalAccessUpdateRequest?.ImportedByName ||
        (externalAccessUpdateRequest?.ImportedBy ? `User #${externalAccessUpdateRequest.ImportedBy}` : '');
    const updateRequestBadge = getUpdateRequestStatusBadge(externalAccessUpdateRequest?.Status);
    const sentSummary = formatDateWithActor(
        externalAccessUpdateRequest?.SentDate,
        externalAccessUpdateRequest?.SenderName || ''
    );
    const importedSummary = formatDateWithActor(externalAccessUpdateRequest?.ImportedDate, importedByName);
    const updateFormUrl = externalAccessUpdateRequest?.Url || externalAccessUpdateRequest?.URL || '';
    const updateFormRecipientEmail = `${externalAccessUpdateRequest?.ContactEmail || externalAccessEmail || ''}`.trim();
    const updateRequestEmailHref = buildUpdateRequestEmailHref({
        recipientEmail: updateFormRecipientEmail,
        updateFormUrl,
    });

    return {
        actionError: externalAccessActionError,
        actionSuccess: externalAccessActionSuccess,
        availableEmails,
        canSendUpdateRequest,
        canUpdateExternalAccess,
        externalAccessActive,
        externalAccessActivationDate,
        externalAccessEmail,
        externalAccessExists,
        externalAccessLoadError,
        externalAccessUpdateRequest,
        handleChangeSelectedEmail: setSelectedEmail,
        handleCloseSendUpdateRequestDrawer: () => setShowSendUpdateRequestDrawer(false),
        handleEnableAndOpenSendUpdateRequestDrawer,
        handleOpenSubmittedUpdateReview,
        handleRevokeExternalAccess,
        handleSendUpdateRequest,
        handleToggleExternalAccessUpdateForm,
        handleUpdateExternalAccess,
        hasAvailableEmails,
        hasImportedUpdateRequest,
        hasPendingSubmittedUpdateRequest,
        hasSubmittedUpdateRequest,
        importedSummary,
        isExternalAccessActionInProgress,
        isLoadingExternalAccess: isLoadingExternalAccess || isLoadingEmails,
        isRevokingExternalAccess,
        isSentUpdateRequest,
        isSendingExternalRequest,
        isUpdatingExternalAccess,
        selectedEmail,
        sentSummary,
        showExternalAccessUpdateForm,
        showSendUpdateRequestDrawer,
        updateFormUrl,
        updateRequestBadge,
        updateRequestEmailHref,
    };
};

export default useExternalAccessActionItem;
